import React, {Dispatch, SetStateAction, useEffect} from "react";
import {useSelector} from "react-redux";
import timerange from "../../store/reducers/timerange";
import {
    AggregateMonitorEvaluationPayload,
    AggregationFunction,
    EvalType,
    MetricAlert,
    MissingDatapointBehavior,
    MonitorEvaluation,
    monitorEvents,
    StaticMonitorEvaluationPayload,
    WindowUnit
} from "../../pages/alerts/MetricAlert";
import {MetoroMetricsChartProps, MetricType, Threshold, TimePeriod} from "../../pages/MetricsTest";
import {EmbedMetricSelector} from "../Dashboarding/widgets/MetricSelector";
import {TraceAlert} from "../../pages/alerts/TraceAlert";
import {useDebouncedCallback} from "use-debounce";
import {cn} from "../ui/lib/utils";
import {Tooltip, TooltipContent, TooltipTrigger} from "../ui/tooltip";
import {Input} from "../ui/input";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger
} from "../ui/dropdown-menu";
import {ChevronDownIcon} from "@radix-ui/react-icons";
import {DropDownItem} from "../Input/MultiSelectorDropdown/MultiSelectorDropDown";
import {SingleSelectDropdown} from "../Input/SingleSelectDropdown";
import {z} from "zod";
import {Button} from "../ui/button";
import {TimeRange} from "../../types/time";
import {capitalizeFirstLetter} from "../../utility/stringUtils";
import {
    createMultiMetricRequest,
    GetAggregateMetricEvaluation,
    GetAggregateMultiMetricEvaluation,
    GetMultiMetrics,
    Metric
} from "../../clients/metoro/metrics";
import {formatValueWithUnit as formatValueWithUnitConverter} from "../../utility/unitConverter";
import {ChartType} from "../Charts/MetoroChart";
import {KubernetesResourceAlert} from "../../pages/alerts/KubernetesResourceAlert";
import {EmbedMultiMetricSelector} from "../Dashboarding/widgets/MultiMetricSelector";
import {MultiMetoroMetricsChartProps} from "../Charts/MultiMetoroMetricsCharts";
import {AlertMultiMetricSelector} from "./AlertMultiMetricSelector";
import {MultiMetricAlert} from "../../pages/alerts/MultiMetricAlert";
import {Alert, AlertType, ThresholdComparator} from "../../pages/alerts/model";
import {useFeatureFlagsStore} from "../../store/featureFlagsStore";

const FormSchema = z.object({
    thresholdValue: z.coerce.number()
        .refine(val => !isNaN(val), {
            message: "Please enter a valid number"
        })
})

export function AlertMetricSelector(props: {
    alert: Alert;
    setAlert: Dispatch<SetStateAction<Alert>>
}) {
    if (props.alert.type === AlertType.Metric) {
        return <MetricSelector alert={props.alert} setAlert={props.setAlert} stepNumber={3}/>;
    }

    if (props.alert.type === AlertType.Trace) {
        return <TraceSelector alert={props.alert} setAlert={props.setAlert} stepNumber={3}/>;
    }

    if (props.alert.type === AlertType.KubernetesResource) {
        return <KubernetesResourceSelector alert={props.alert} setAlert={props.setAlert} stepNumber={3}/>;
    }

    if (props.alert.type === AlertType.MultiMetric) {
        return <AlertMultiMetricSelector alert={props.alert} setAlert={props.setAlert}/>;
    }

    return <></>
}

function MetricSelector(props: {
    alert: Alert,
    setAlert: Dispatch<SetStateAction<Alert>>
    stepNumber: number
}) {
    const timeRange = useSelector(timerange.selectors.getTimeRange)
    const typedMetricAlert = props.alert.alert as MetricAlert
    const [chartProps, setChartProps] = React.useState<MetoroMetricsChartProps>(initialChartProps());

    function initialChartProps(): MetoroMetricsChartProps {
        return {
            startTime: Math.floor(timeRange.getStartEnd()[0].getTime() / 1000),
            endTime: Math.floor(timeRange.getStartEnd()[1].getTime() / 1000),
            metricName: typedMetricAlert.filters.metricName != "" ? typedMetricAlert.filters.metricName : "container_net_tcp_active_connections",
            excludeFilters: typedMetricAlert.filters.excludeFilters,
            filters: typedMetricAlert.filters.filters,
            splits: typedMetricAlert.filters.splits,
            aggregation: typedMetricAlert.filters.aggregation != "" ? typedMetricAlert.filters.aggregation : "avg",
            type: ChartType.Line,
            metricType: MetricType.Metric,
            functions: typedMetricAlert.filters.functions,
            bucketSize: typedMetricAlert.filters.bucketSize ? typedMetricAlert.filters.bucketSize : 60 // Default 1 minute bucket size
        }
    }

    useEffect(() => {
        setChartProps((prev) => {
            return {
                ...prev,
                startTime: Math.floor(timeRange.getStartEnd()[0].getTime() / 1000),
                endTime: Math.floor(timeRange.getStartEnd()[1].getTime() / 1000)
            }
        })
    }, [timeRange]);


    useEffect(() => {
        const annotationProperties = getGraphAnnotationProperties(
            typedMetricAlert.monitorEvaluation,
            timeRange, typedMetricAlert.eventPayload.threshold,
            typedMetricAlert.eventPayload.condition as ThresholdComparator
        )
        const thresholdToSet = annotationProperties.thresholdToSet
        const timePeriodToHighlight = annotationProperties.timePeriodToHighlight

        setChartProps((prev) => {
            return {
                ...prev,
                threshold: thresholdToSet,
                timePeriodHighlight: timePeriodToHighlight,
                thresholdLabel: annotationProperties.annotationLabel
            }
        })
    }, [typedMetricAlert.monitorEvaluation.monitorEvaluationType,
        typedMetricAlert.monitorEvaluation.monitorEvalutionPayload,
        typedMetricAlert.eventPayload.threshold,
        typedMetricAlert.eventPayload.condition,
        typedMetricAlert.filters.aggregation,
        timeRange,
        (typedMetricAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload)?.window,
        (typedMetricAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload)?.windowUnit]);

    useEffect(() => {
        props.setAlert((prev) => {
            let metricAlert = prev.alert as MetricAlert
            let metricFilter = {...metricAlert.filters}
            metricFilter.metricName = chartProps.metricName
            metricFilter.aggregation = chartProps.aggregation
            metricFilter.bucketSize = chartProps.bucketSize
            metricFilter.filters = chartProps.filters ? chartProps.filters : new Map()
            metricFilter.excludeFilters = chartProps.excludeFilters ? chartProps.excludeFilters : new Map()
            metricFilter.splits = chartProps.splits ? chartProps.splits : []
            metricFilter.functions = chartProps.functions ? chartProps.functions : []
            metricAlert.filters = metricFilter
            return {...prev, alert: metricAlert}
        })
    }, [chartProps]);


    return <div className={"flex flex-col gap-8"}>
        <div className={"flex flex-col gap-4"}>
            <div className={"flex justify-start gap-2"}>
                <div
                    className={"h-[36px] w-[36px] border border-primary bg-primarytransparent text-textlight flex flex-col justify-center text-center font-semibold rounded"}>
                    2
                </div>
                <div
                    className={"text-lg flex flex-col justify-center text-center text-textlight"}>
                    Select Metric
                </div>
            </div>
            <EmbedMetricSelector chartProps={chartProps}
                                 setChartProps={setChartProps}/>
        </div>
        <EventSelector stepNumber={props.stepNumber}
                       alert={props.alert}
                       setAlert={props.setAlert}

        />
    </div>
}

function KubernetesResourceSelector(props: {
    alert: Alert,
    setAlert: Dispatch<SetStateAction<Alert>>
    stepNumber: number
}) {
    const timeRange = useSelector(timerange.selectors.getTimeRange)
    const typedKubernetesResourceAlert = props.alert.alert as KubernetesResourceAlert

    function initialChartProps(): MultiMetoroMetricsChartProps {
        return {
            startTime: Math.floor(timeRange.getStartEnd()[0].getTime() / 1000),
            endTime: Math.floor(timeRange.getStartEnd()[1].getTime() / 1000),
            metricSpecifiers: [{
                metricName: "kubernetes_resource",
                excludeFilters: typedKubernetesResourceAlert.filters.excludeFilters,
                filters: typedKubernetesResourceAlert.filters.filters,
                splits: typedKubernetesResourceAlert.filters.splits,
                aggregation: typedKubernetesResourceAlert.filters.aggregation != "" ? typedKubernetesResourceAlert.filters.aggregation : "count",
                functions: typedKubernetesResourceAlert.filters.functions,
                metricType: MetricType.Kubernetes,
                bucketSize: typedKubernetesResourceAlert.filters.bucketSize ? typedKubernetesResourceAlert.filters.bucketSize : 60, // Default 1 minute bucket size
                jsonPath: typedKubernetesResourceAlert.filters.jsonPath
            }],
            type: ChartType.Line,
        }
    }

    const [chartProps, setChartProps] = React.useState<MultiMetoroMetricsChartProps>(initialChartProps());


    useEffect(() => {
        setChartProps((prev) => {
            return {
                ...prev,
                startTime: Math.floor(timeRange.getStartEnd()[0].getTime() / 1000),
                endTime: Math.floor(timeRange.getStartEnd()[1].getTime() / 1000)
            }
        })
    }, [timeRange]);

    useEffect(() => {
        const annotationProperties = getGraphAnnotationProperties(
            typedKubernetesResourceAlert.monitorEvaluation,
            timeRange,
            typedKubernetesResourceAlert.eventPayload.threshold,
            typedKubernetesResourceAlert.eventPayload.condition as ThresholdComparator)
        const thresholdToSet = annotationProperties.thresholdToSet
        const timePeriodToHighlight = annotationProperties.timePeriodToHighlight

        setChartProps((prev) => {
            return {
                ...prev,
                threshold: thresholdToSet,
                timePeriodHighlight: timePeriodToHighlight,
                thresholdLabel: annotationProperties.annotationLabel
            }
        })
    }, [typedKubernetesResourceAlert.monitorEvaluation.monitorEvaluationType,
        typedKubernetesResourceAlert.monitorEvaluation.monitorEvalutionPayload,
        typedKubernetesResourceAlert.eventPayload.threshold,
        typedKubernetesResourceAlert.eventPayload.condition,
        typedKubernetesResourceAlert.filters.aggregation]);

    useEffect(() => {
        props.setAlert((prev) => {
            let kubernetesAlert = prev.alert as KubernetesResourceAlert
            let kubernetesFilter = {...kubernetesAlert.filters}
            kubernetesFilter.aggregation = chartProps.metricSpecifiers[0].aggregation
            kubernetesFilter.bucketSize = chartProps.metricSpecifiers[0].bucketSize
            kubernetesFilter.filters = chartProps.metricSpecifiers[0].filters ? chartProps.metricSpecifiers[0].filters : new Map()
            kubernetesFilter.excludeFilters = chartProps.metricSpecifiers[0].excludeFilters ? chartProps.metricSpecifiers[0].excludeFilters : new Map()
            kubernetesFilter.splits = chartProps.metricSpecifiers[0].splits ? chartProps.metricSpecifiers[0].splits : []
            kubernetesFilter.functions = chartProps.metricSpecifiers[0].functions ? chartProps.metricSpecifiers[0].functions : []
            kubernetesAlert.filters = kubernetesFilter
            kubernetesFilter.jsonPath = chartProps.metricSpecifiers[0].jsonPath

            return {...prev, alert: kubernetesAlert}
        })
    }, [chartProps]);


    return <div className={"flex flex-col gap-8"}>
        <div className={"flex flex-col gap-4"}>
            <div className={"flex justify-start gap-2"}>
                <div
                    className={"h-[36px] w-[36px] border border-primary bg-primarytransparent text-textlight flex flex-col justify-center text-center font-semibold rounded"}>
                    2
                </div>
                <div
                    className={"text-lg flex flex-col justify-center text-center text-textlight"}>
                    Select Kubernetes Resource Attributes
                </div>
            </div>
            <EmbedMultiMetricSelector chartProps={chartProps}
                                      setChartProps={setChartProps}/>
        </div>
        <EventSelector stepNumber={props.stepNumber}
                       alert={props.alert}
                       setAlert={props.setAlert}
        />
    </div>
}


function TraceSelector(props: {
    alert: Alert,
    setAlert: Dispatch<SetStateAction<Alert>>
    stepNumber: number
}) {
    const timeRange = useSelector(timerange.selectors.getTimeRange)
    const typedTraceAlert = props.alert.alert as TraceAlert
    const [chartProps, setChartProps] = React.useState<MetoroMetricsChartProps>(initialChartProps());

    function initialChartProps(): MetoroMetricsChartProps {
        return {
            startTime: Math.floor(timeRange.getStartEnd()[0].getTime() / 1000),
            endTime: Math.floor(timeRange.getStartEnd()[1].getTime() / 1000),
            metricName: "container_net_tcp_active_connections",
            excludeFilters: typedTraceAlert.filters.excludeFilters,
            filters: typedTraceAlert.filters.filters,
            splits: typedTraceAlert.filters.splits,
            aggregation: typedTraceAlert.filters.aggregation != "" ? typedTraceAlert.filters.aggregation : "count",
            type: ChartType.Line,
            metricType: MetricType.Trace,
            functions: typedTraceAlert.filters.functions,
            bucketSize: typedTraceAlert.filters.bucketSize ? typedTraceAlert.filters.bucketSize : 60 // Default 1 minute bucket size
        }
    }

    useEffect(() => {
        setChartProps((prev) => {
            return {
                ...prev,
                startTime: Math.floor(timeRange.getStartEnd()[0].getTime() / 1000),
                endTime: Math.floor(timeRange.getStartEnd()[1].getTime() / 1000)
            }
        })
    }, [timeRange]);

    useEffect(() => {
        const annotationProperties = getGraphAnnotationProperties(
            typedTraceAlert.monitorEvaluation,
            timeRange,
            typedTraceAlert.eventPayload.threshold,
            typedTraceAlert.eventPayload.condition as ThresholdComparator)
        const thresholdToSet = annotationProperties.thresholdToSet
        const timePeriodToHighlight = annotationProperties.timePeriodToHighlight

        setChartProps((prev) => {
            return {
                ...prev,
                threshold: thresholdToSet,
                timePeriodHighlight: timePeriodToHighlight,
                thresholdLabel: annotationProperties.annotationLabel
            }
        })
    }, [typedTraceAlert.monitorEvaluation.monitorEvaluationType,
        typedTraceAlert.monitorEvaluation.monitorEvalutionPayload,
        typedTraceAlert.eventPayload.threshold,
        typedTraceAlert.eventPayload.condition,
        typedTraceAlert.filters.aggregation]);

    useEffect(() => {
        props.setAlert((prev) => {
            let traceAlert = prev.alert as TraceAlert
            let traceFilter = {...traceAlert.filters}
            traceFilter.aggregation = chartProps.aggregation
            traceFilter.bucketSize = chartProps.bucketSize
            traceFilter.filters = chartProps.filters ? chartProps.filters : new Map()
            traceFilter.excludeFilters = chartProps.excludeFilters ? chartProps.excludeFilters : new Map()
            traceFilter.splits = chartProps.splits ? chartProps.splits : []
            traceFilter.functions = chartProps.functions ? chartProps.functions : []
            traceAlert.filters = traceFilter
            return {...prev, alert: traceAlert}
        })
    }, [chartProps]);


    return <div className={"flex flex-col gap-8"}>
        <div className={"flex flex-col gap-4"}>
            <div className={"flex justify-start gap-2"}>
                <div
                    className={"h-[36px] w-[36px] border border-primary bg-primarytransparent text-textlight flex flex-col justify-center text-center font-semibold rounded"}>
                    2
                </div>
                <div
                    className={"text-lg flex flex-col justify-center text-center text-textlight"}>
                    Select Trace Attributes
                </div>
            </div>
            <EmbedMetricSelector chartProps={chartProps}
                                 setChartProps={setChartProps}/>
        </div>
        <EventSelector stepNumber={props.stepNumber}
                       alert={props.alert}
                       setAlert={props.setAlert}
        />
    </div>
}

export function EventSelector(props: {
    stepNumber: number
    alert: Alert,
    setAlert: Dispatch<SetStateAction<Alert>>
}) {
    const [metricAggregateEvaluation, setMetricAggregateEvaluation] = React.useState<Metric>();
    const debouncedUpdateMetricAggregateEvaluation = useDebouncedCallback(updateMetricAggregateEvaluation, 10);
    const debouncedUpdateMultiMetricAggregateEvaluation = useDebouncedCallback(updateMetricAggregateEvaluationMulti, 10);
    const startEnd = useSelector(timerange.selectors.getTimeRange).getStartEnd()
    const typedAlert = props.alert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert
    const featureFlags = useFeatureFlagsStore();
    const [isAnomalyDetectionEnabled, setIsAnomalyDetectionEnabled] = React.useState<boolean>(false);

    // Load feature flag
    useEffect(() => {
        featureFlags.featureFlagEnabled("alerts.anomalyDetection").then((enabled) => {
            setIsAnomalyDetectionEnabled(enabled);
        });
    }, [featureFlags]);

    useEffect(() => {
        if (typedAlert.monitorEvaluation.monitorEvaluationType === EvalType.Aggregate && props.alert.type !== "MultiMetric") {
            const endTime = Math.floor(startEnd[1].getTime() / 1000)
            const payload = typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload
            let startTime = Math.floor(startEnd[0].getTime() / 1000)
            if (payload !== undefined) {
                startTime = endTime - payload.window * getWindowUnitSeconds(payload.windowUnit)
            }
            debouncedUpdateMetricAggregateEvaluation(props.alert, startTime, endTime, setMetricAggregateEvaluation);
        }
    }, [props.alert]);

    useEffect(() => {
        if (typedAlert.monitorEvaluation.monitorEvaluationType === EvalType.Aggregate && props.alert.type === "MultiMetric") {
            const endTime = Math.floor(startEnd[1].getTime() / 1000)
            const payload = typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload
            let startTime = Math.floor(startEnd[0].getTime() / 1000)
            if (payload !== undefined) {
                startTime = endTime - payload.window * getWindowUnitSeconds(payload.windowUnit)
            }
            debouncedUpdateMultiMetricAggregateEvaluation(props.alert, startTime, endTime, setMetricAggregateEvaluation);
        }
    }, [props.alert]);

    async function updateMetricAggregateEvaluationMulti(alert: Alert,
                                                        startTime: number,
                                                        endTime: number,
                                                        setMetric: (value: (((prevState: (Metric | undefined)) => (Metric | undefined)) | Metric | undefined)) => void) {

        const typedAlert = alert.alert as MultiMetricAlert
        try {
            const awaitedMetrics = await GetAggregateMultiMetricEvaluation(createMultiMetricRequest(typedAlert.filters.metricSpecifiers, startTime, endTime, true, typedAlert.filters.formula ? [typedAlert.filters.formula] : []), typedAlert.monitorEvaluation.monitorEvalutionPayload);
            setMetric(awaitedMetrics.metric);
        } catch (e) {
            console.error(e);
        }
    }

    async function updateMetricAggregateEvaluation(alert: Alert,
                                                   startTime: number,
                                                   endTime: number,
                                                   setMetric: (value: (((prevState: (Metric | undefined)) => (Metric | undefined)) | Metric | undefined)) => void) {

        const typedAlert = alert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert
        let filters = typedAlert.filters.filters;
        if (filters === undefined) {
            filters = new Map<string, string[]>();
        }
        let excludeFilters = typedAlert.filters.excludeFilters;
        if (excludeFilters === undefined) {
            excludeFilters = new Map<string, string[]>();
        }

        let splits = typedAlert.filters.splits;
        if (splits === undefined) {
            splits = [];
        }
        try {
            let type = MetricType.Trace
            let metricName = ""
            if (alert.type === AlertType.Metric) {
                type = MetricType.Metric
                metricName = (alert.alert as MetricAlert).filters.metricName
            }
            let jsonPath = undefined
            let aggregateParams = typedAlert.monitorEvaluation.monitorEvalutionPayload!;
            if (alert.type === AlertType.KubernetesResource) {
                type = MetricType.Kubernetes
                jsonPath = (alert.alert as KubernetesResourceAlert).filters.jsonPath
                aggregateParams.jsonPath = jsonPath
            }

            const request = {
                type: type,
                metricName: metricName,
                startTime: startTime,
                endTime: endTime,
                filters: filters,
                excludeFilters: excludeFilters,
                splits: splits,
                aggregation: typedAlert.filters.aggregation,
                isRate: false,
                functions: typedAlert.filters.functions,
                aggregateParams: aggregateParams,
                limitResults: false, // we should never limit results for aggregation. This field is ignored by get aggregation metric endpoint in the backend anyway.
            };
            const awaitedMetrics = await GetAggregateMetricEvaluation(request);
            setMetric(awaitedMetrics.metric);
        } catch (e) {
            console.error(e);
        }
    }

    return <div className={"flex items-start gap-x-32"}>
        <div className={"flex flex-col justify-between"}>
            <div className={"flex justify-start gap-2"}>
                <div
                    className={"h-[36px] w-[36px] border border-primary bg-primarytransparent text-textlight flex flex-col justify-center text-center font-semibold rounded"}>
                    {props.stepNumber}
                </div>
                <div className={"text-lg flex flex-col justify-center text-center text-textlight"}>
                    Select Alarm Condition
                </div>
            </div>
            <div className={"pl-2 flex flex-col gap-4"}>
                <div className={"flex text-textdark font-semibold items-baseline"}>
                    <div className={"w-[230px]"}>
                        • Select Threshold Type:
                    </div>
                    <div className={"flex gap-2 items-center"}>
                        {
                            monitorEvents.filter(x => x.monitorEvaluationType !== EvalType.Anomaly || (isAnomalyDetectionEnabled && props.alert.type == AlertType.MultiMetric)).map((event, index) => {
                                return <div key={index}
                                            className={cn("h-[32px] flex justify-center items-center text-textmedium border gap-2 p-2 rounded bg-none border-buttonborder hover:text-textlight hover:border hover:border-secondary cursor-pointer px-4", (props.alert.alert as MetricAlert | TraceAlert).monitorEvaluation.monitorEvaluationType === event.monitorEvaluationType || monitorEvents.length === 1 ? "border bg-secondarytransparenter border-secondary" : "")}
                                            onClick={() => {
                                                let typedalert = props.alert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert
                                                typedalert.monitorEvaluation = event
                                                props.setAlert({...props.alert, alert: typedalert})
                                            }}>
                                    <Tooltip delayDuration={10}>
                                        <TooltipTrigger>
                                            {capitalizeFirstLetter(event.monitorEvaluationType)}
                                        </TooltipTrigger>
                                        <TooltipContent side={"right"}
                                                        className={"ml-4 bg-backgroundlight border text-textlight rounded"}>
                                            {event.description}
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                            })
                        }
                    </div>
                </div>
                <AlarmEvaluationPropertySelector alert={props.alert} setAlert={props.setAlert}/>
                <AlarmConditionSelector alert={props.alert}
                                        setAlert={props.setAlert}
                />
            </div>
        </div>
    </div>
}

function AlarmConditionSelector(props: {
    alert: Alert,
    setAlert: Dispatch<SetStateAction<Alert>>,
}) {
    const typedAlert = props.alert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert
    const [thresholdComparator, setThresholdComparator] = React.useState(typedAlert.eventPayload.condition)
    const [thresholdValue, setThresholdValue] = React.useState(typedAlert.eventPayload.threshold)
    const [error, setError] = React.useState<string>("")
    const [metricUnit, setMetricUnit] = React.useState<string>("")
    const [abortController, setAbortController] = React.useState(new AbortController());
    const timeRange = useSelector(timerange.selectors.getTimeRange)

    // Check if anomaly detection is enabled
    const isAnomalyDetection = typedAlert.monitorEvaluation.monitorEvaluationType === EvalType.Anomaly

    // Fetch metric unit based on alert type
    useEffect(() => {
        const fetchMetricUnit = async () => {
            try {
                const alertData = props.alert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert;
                let unit = "";

                if (props.alert.type === AlertType.MultiMetric) {
                    const multiMetricAlert = alertData as MultiMetricAlert;
                    if (multiMetricAlert.filters.metricSpecifiers.length > 0) {
                        const startTime = Math.floor(timeRange.getStartEnd()[0].getTime() / 1000);
                        const endTime = Math.floor(timeRange.getStartEnd()[1].getTime() / 1000);

                        const request = createMultiMetricRequest(
                            multiMetricAlert.filters.metricSpecifiers.slice(0, 1), // Just get the first metric
                            startTime,
                            endTime,
                            false,
                            multiMetricAlert.filters.formula ? [multiMetricAlert.filters.formula] : undefined
                        );

                        const response = await GetMultiMetrics(request, abortController, setAbortController);
                        if (response.metrics && response.metrics.length > 0 && response.metrics[0].metric) {
                            unit = response.metrics[0].metric.unit || "";
                        }
                    }
                }

                setMetricUnit(unit);
            } catch (error) {
                console.error("Failed to fetch metric unit:", error);
            }
        };

        fetchMetricUnit();
    }, [props.alert.alert.filters, props.alert.alert, timeRange]);

    useEffect(() => {
        props.setAlert((prev) => {
            let metricAlert = prev.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert
            metricAlert.eventPayload = {condition: thresholdComparator, threshold: thresholdValue}
            return {...prev, alert: metricAlert}
        })
    }, [thresholdComparator, thresholdValue]);

    const handleThresholdChange = (value: string) => {
        const numberValue = Number(value)
        if (isNaN(numberValue)) {
            setError("Please enter a valid number")
            return
        }
        setError("")
        setThresholdValue(numberValue)
    }

    function getPlaceholderString() {
        switch (thresholdComparator) {
            case ThresholdComparator.GreaterThan:
                return "Greater than..."
            case ThresholdComparator.GreaterThanOrEqual:
                return "Greater than or equal to..."
            case ThresholdComparator.LessThanOrEqual:
                return "Lower than or equal to..."
            case ThresholdComparator.LessThan:
                return "Lower than..."
        }
    }

    if (isAnomalyDetection) {
        return <div className={"flex flex-col gap-4"}>
            <div className={"flex items-baseline"}>
                <div className={"text-textdark font-semibold w-[230px]"}> • Anomaly Detection Info:</div>
                <div className="flex flex-col grow shrink space-y-2">
                    <div className="text-textmedium">
                        Anomaly detection uses the Z-score algorithm to identify outliers.
                    </div>
                    <div className="text-textdark text-sm">
                        The alert will trigger when the metric value deviates from the baseline by more than the
                        configured Z-score threshold.
                    </div>
                    <div className="text-textdark text-sm">
                        Configure the Z-score threshold and data window in the evaluation settings above.
                    </div>
                </div>
            </div>
        </div>
    }

    return <div className={"flex flex-col gap-4"}>
        <div className={"flex items-baseline"}>
            <div className={"text-textdark font-semibold text-nowrap w-[230px]"}> • Select Threshold Comparator:
            </div>
            <div className={"flex gap-2 justify-start items-start text-center grow shrink"}>
                <Button
                    className={cn("h-[32px] flex justify-center items-center text-textmedium border gap-2 p-2 rounded hover:text-textlight hover:border hover:border-secondary cursor-pointer px-4", thresholdComparator === ThresholdComparator.GreaterThan ? " bg-secondarytransparenter border-secondary" : "bg-backgroundmedium")}
                    onClick={() => {
                        setThresholdComparator(ThresholdComparator.GreaterThan)
                    }}>
                    Greater &gt;
                </Button>
                <Button
                    className={cn("h-[32px] flex justify-center items-center text-textmedium border gap-2 p-2 rounded hover:text-textlight hover:border hover:border-secondary cursor-pointer px-4", thresholdComparator === ThresholdComparator.GreaterThanOrEqual ? " bg-secondarytransparenter border-secondary" : "bg-backgroundmedium")}
                    onClick={() => {
                        setThresholdComparator(ThresholdComparator.GreaterThanOrEqual)
                    }}>
                    Greater / Equal &gt;=
                </Button>
                <Button
                    className={cn("h-[32px] flex justify-center items-center text-textmedium border gap-2 p-2 rounded hover:text-textlight hover:border hover:border-secondary cursor-pointer px-4", thresholdComparator === ThresholdComparator.LessThanOrEqual ? " bg-secondarytransparenter border-secondary" : "bg-backgroundmedium")}
                    onClick={() => {
                        setThresholdComparator(ThresholdComparator.LessThanOrEqual)
                    }}>
                    Lower / Equal &lt;=
                </Button>
                <Button
                    className={cn("h-[32px] flex justify-center items-center text-textmedium border gap-2 p-2 rounded hover:text-textlight hover:border hover:border-secondary cursor-pointer px-4", thresholdComparator === ThresholdComparator.LessThan ? " bg-secondarytransparenter border-secondary" : "bg-backgroundmedium")}
                    onClick={() => {
                        setThresholdComparator(ThresholdComparator.LessThan)
                    }}>
                    Lower &lt;
                </Button>
            </div>
        </div>
        <div className={"flex items-baseline"}>
            <div className={"text-textdark font-semibold w-[230px]"}> • Define Threshold Value:</div>
            <div className="flex flex-col grow shrink space-y-2">
                <div className="flex items-center gap-2">
                    <Input
                        type="number"
                        value={thresholdValue}
                        onChange={(e) => handleThresholdChange(e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        className={"bg-backgroundmedium border-border text-textmedium border rounded w-[400px]"}
                        placeholder={`${getPlaceholderString()}`}
                    />
                    {metricUnit && (
                        <div className="flex grow items-center gap-2 text-textmedium">
                            <span className="text-sm font-medium">{metricUnit}</span>
                            {thresholdValue > 0 && (
                                <span className="text-sm flex grow text-textdark">
                                    ({formatValueWithUnitConverter(thresholdValue, metricUnit, 2, {
                                    includeTime: true,
                                    includeMillis: false
                                })})
                                </span>
                            )}
                        </div>
                    )}
                </div>
                {error && <div className="text-red-500 text-sm">{error}</div>}
                <div className="text-textdark text-sm">
                    The threshold is defined in terms of the base unit of the metric
                    {metricUnit && " - " + metricUnit}
                </div>
            </div>
        </div>
    </div>
}


function AlarmEvaluationPropertySelector(props: { alert: Alert, setAlert: Dispatch<SetStateAction<Alert>> }) {
    const typedAlert = props.alert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert
    if (typedAlert.monitorEvaluation.monitorEvaluationType === EvalType.Static) {
        return <StaticAlarmEvaluationProperties alert={props.alert} setAlert={props.setAlert}/>
    }
    if (typedAlert.monitorEvaluation.monitorEvaluationType === EvalType.Aggregate) {
        return <AggregateAlarmEvaluationProperties alert={props.alert} setAlert={props.setAlert}/>
    }
    if (typedAlert.monitorEvaluation.monitorEvaluationType === EvalType.Anomaly) {
        return <AnomalyAlarmEvaluationProperties alert={props.alert} setAlert={props.setAlert}/>
    }
    return <></>
}

function StaticAlarmEvaluationProperties(props: { alert: Alert, setAlert: Dispatch<SetStateAction<Alert>> }) {
    const typedAlert = props.alert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert
    const typedMonitorEvaluationPayload = typedAlert.monitorEvaluation.monitorEvalutionPayload as StaticMonitorEvaluationPayload;

    useEffect(() => {
        if (typedMonitorEvaluationPayload.evaluationWindow === undefined || typedMonitorEvaluationPayload.datapointsToAlarm === undefined) {
            props.setAlert((prev) => {
                const newAlert = {...prev};
                const typedNewAlert = newAlert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert;
                typedNewAlert.monitorEvaluation.monitorEvalutionPayload = {
                    ...typedNewAlert.monitorEvaluation.monitorEvalutionPayload,
                    evaluationWindow: 1,
                    datapointsToAlarm: 1,
                };
                return newAlert;
            });
        }
    }, []);

    return <div className={"flex flex-col gap-4"}>
        <div className={"flex text-textdark font-semibold items-baseline"}>
            <div className={"w-[230px]"}>
                • Select Evaluation Method:
            </div>
            <div className={"flex gap-2 items-center"}>
                <div className={"w-max text-nowrap"}>alarm when</div>
                <Input
                    className={"w-[40px] text-center h-full p-1"}
                    type="number"
                    value={(typedAlert.monitorEvaluation.monitorEvalutionPayload as StaticMonitorEvaluationPayload)?.datapointsToAlarm}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (isNaN(value)) return;

                        props.setAlert(prev => {
                            const newAlert = {...prev};
                            const typedNewAlert = newAlert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert;
                            if (!typedNewAlert.monitorEvaluation.monitorEvalutionPayload) {
                                typedNewAlert.monitorEvaluation.monitorEvalutionPayload = {
                                    missingDatapointBehavior: (typedNewAlert.monitorEvaluation.monitorEvalutionPayload! as StaticMonitorEvaluationPayload).missingDatapointBehavior || MissingDatapointBehavior.NotBreaching,
                                    evaluationWindow: (typedNewAlert.monitorEvaluation.monitorEvalutionPayload! as StaticMonitorEvaluationPayload).evaluationWindow || 1,
                                    datapointsToAlarm: value
                                };
                            } else {
                                (typedNewAlert.monitorEvaluation.monitorEvalutionPayload as StaticMonitorEvaluationPayload).datapointsToAlarm = value;
                            }
                            return newAlert;
                        });
                    }}
                />
                <div className={"w-max text-nowrap"}> out of</div>
                <Input
                    className={"w-[40px] text-center h-full p-1"}
                    type="number"
                    value={(typedAlert.monitorEvaluation.monitorEvalutionPayload as StaticMonitorEvaluationPayload).evaluationWindow}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (isNaN(value)) return;

                        props.setAlert(prev => {
                            const newAlert = {...prev};
                            const typedNewAlert = newAlert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert;
                            if (!typedNewAlert.monitorEvaluation.monitorEvalutionPayload) {
                                typedNewAlert.monitorEvaluation.monitorEvalutionPayload = {
                                    missingDatapointBehavior: (typedNewAlert.monitorEvaluation.monitorEvalutionPayload! as StaticMonitorEvaluationPayload).missingDatapointBehavior || MissingDatapointBehavior.NotBreaching,
                                    evaluationWindow: value,
                                    datapointsToAlarm: (typedNewAlert.monitorEvaluation.monitorEvalutionPayload! as StaticMonitorEvaluationPayload).datapointsToAlarm || 1
                                };
                            } else {
                                (typedNewAlert.monitorEvaluation.monitorEvalutionPayload as StaticMonitorEvaluationPayload).evaluationWindow = value;
                            }
                            return newAlert;
                        });
                    }}
                />
                <div className={"w-max text-nowrap"}>datapoints breach the threshold.</div>
            </div>
        </div>
        <MissingDataBehaviorSelector alert={props.alert} setAlert={props.setAlert}/>

    </div>
}

function MissingDataBehaviorSelector(props: { alert: Alert, setAlert: Dispatch<SetStateAction<Alert>> }) {
    const typedAlert = props.alert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert
    const missingDatapointOption: DropDownItem[] = [
        {displayName: "breaching the threshold", value: MissingDatapointBehavior.Breaching},
        {displayName: "not breaching the threshold", value: MissingDatapointBehavior.NotBreaching}]
    const typedMonitorEvaluationPayload = typedAlert.monitorEvaluation.monitorEvalutionPayload as StaticMonitorEvaluationPayload
    const [missingDataBehavior, setMissingDataBehavior] = React.useState(missingDatapointOption.find((item) => item.value === typedMonitorEvaluationPayload.missingDatapointBehavior) || missingDatapointOption[0])

    useEffect(() => {
        props.setAlert((prev) => {
            const typedAlert = prev.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert
            const typedMonitorEvaluationPayload = typedAlert.monitorEvaluation.monitorEvalutionPayload as StaticMonitorEvaluationPayload;
            typedMonitorEvaluationPayload.missingDatapointBehavior = missingDataBehavior.value as MissingDatapointBehavior;
            typedAlert.monitorEvaluation.monitorEvalutionPayload = typedMonitorEvaluationPayload;
            return {...prev, alert: typedAlert}
        })
    }, [missingDataBehavior]);


    return <div className={"flex text-textdark font-semibold items-baseline"}>
        <div className={"text-textdark font-semibold w-[230px]"}>
            • Select Missing Data Handling:
        </div>
        <div className={"h-[32px]"}>
            <SingleSelectDropdown selectedItemTitle={"Treat missing datapoint as"}
                                  possibleItems={missingDatapointOption}
                                  selectedItem={missingDataBehavior}
                                  setSelectedItem={setMissingDataBehavior}

            />
        </div>
    </div>
}


function AggregateAlarmEvaluationProperties(props: { alert: Alert, setAlert: Dispatch<SetStateAction<Alert>> }) {
    const typedMetricOrTraceAlert = props.alert.alert as MetricAlert | TraceAlert
    const typedAlert = props.alert.alert as MetricAlert | TraceAlert | MultiMetricAlert | KubernetesResourceAlert
    const typedMonitorEvaluation = typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload

    // Ensure evaluation splits are synced with metric specifier splits
    React.useEffect(() => {
        let splits: string[] = [];

        if (props.alert.type === "MultiMetric") {
            const multiMetricAlert = typedAlert as MultiMetricAlert;
            if (multiMetricAlert.filters.metricSpecifiers && multiMetricAlert.filters.metricSpecifiers.length > 0) {
                splits = multiMetricAlert.filters.metricSpecifiers[0].splits || [];
            }
        } else if (props.alert.type === "Metric") {
            splits = (typedAlert as MetricAlert).filters.splits || [];
        } else if (props.alert.type === "Trace") {
            splits = (typedAlert as TraceAlert).filters.splits || [];
        } else if (props.alert.type === "KubernetesResource") {
            splits = (typedAlert as KubernetesResourceAlert).filters.splits || [];
        }

        if (JSON.stringify(typedMonitorEvaluation.evaluationSplits) !== JSON.stringify(splits)) {
            props.setAlert((prev) => {
                const newAlert = {...prev};
                const newTypedAlert = newAlert.alert as MetricAlert | TraceAlert | MultiMetricAlert | KubernetesResourceAlert;
                (newTypedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload).evaluationSplits = splits;
                return newAlert;
            });
        }
    }, [props.alert]);

    return <div className={"flex text-textdark font-semibold items-baseline"}>
        <div className={"w-[230px]"}>
            • Select Evaluation Function:
        </div>
        <div
            className={"min-h-[48px] min-w-[73px] flex flex-col justify-center bg-backgrounddark items-center text-center text-textmedium border rounded"}>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <div
                        className={"h-[32px] flex justify-center items-center text-textmedium gap-2 pl-3 pr-2 bg-none"}>
                        <div>{typedMonitorEvaluation ? typedMonitorEvaluation.evaluationFunction : AggregationFunction.Sum}</div>
                        <ChevronDownIcon className={"text-textmedium"}/>
                    </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent side={"bottom"}
                                     className="mt-2 bg-background text-textmedium rounded">
                    <DropdownMenuRadioGroup value={"aggregateFunctions"}
                                            onValueChange={(value) => {
                                                let typedalert = props.alert.alert as MetricAlert | TraceAlert | MultiMetricAlert | KubernetesResourceAlert
                                                (typedalert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload).evaluationFunction = value
                                                props.setAlert({...props.alert, alert: typedalert})
                                            }}>
                        {Object.entries(AggregationFunction).map(([key, value], index) => {
                            return <DropdownMenuRadioItem className="hover:bg-backgroundlight"
                                                          key={key}
                                                          value={value}>{value}</DropdownMenuRadioItem>
                        })}
                    </DropdownMenuRadioGroup>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
        <div className={"mx-2"}>
            over
        </div>
        <div className={"flex border rounded"}>
            <div className={"flex items-baseline "}>
                <Tooltip delayDuration={30}>
                    <TooltipTrigger>
                        <Input type={"number"} min={0}
                               value={typedMonitorEvaluation.window}
                               onChange={(e) => {
                                   (typedMetricOrTraceAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload).window = parseInt(e.target.value)
                                   props.setAlert({
                                       ...props.alert,
                                       alert: typedMetricOrTraceAlert
                                   })
                               }}
                               className={"h-[48px] w-[52px] bg-backgroundmedium text-textmedium z-50 border-none"}
                        />
                    </TooltipTrigger>
                    <TooltipContent side={"top"}
                                    className={"bg-backgroundmedium border rounded px-2 py-1 font-normal text-textdark"}>Must
                        be an integer</TooltipContent>
                </Tooltip>
            </div>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <div
                        className={"h-[48px] flex justify-center items-center text-textmedium border-l bg-backgrounddark gap-2 pl-3 pr-2 bg-none border-buttonborder hover:text-textlight hover:border hover:border-secondary cursor-pointer"}>
                        <div>{formatWindowUnit(props.alert)}</div>
                        <ChevronDownIcon className={"text-textmedium"}/>
                    </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent side={"bottom"}
                                     className="w-max bg-backgrounddark text-textmedium rounded">
                    <DropdownMenuRadioGroup value={"aggregateFunctions"}
                                            onValueChange={(value) => {
                                                (typedMetricOrTraceAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload).windowUnit = value as WindowUnit
                                                props.setAlert({
                                                    ...props.alert,
                                                    alert: typedMetricOrTraceAlert
                                                })
                                            }}>
                        {Object.entries(WindowUnit).map(([key, value], index) => {
                            return <DropdownMenuRadioItem
                                className="hover:bg-backgroundlight"
                                key={key}
                                value={value}>{value}</DropdownMenuRadioItem>
                        })}
                    </DropdownMenuRadioGroup>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    </div>
}

function AnomalyAlarmEvaluationProperties(props: { alert: Alert, setAlert: Dispatch<SetStateAction<Alert>> }) {
    const typedAlert = props.alert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert
    const anomalyCondition = typedAlert.monitorEvaluation.anomalyCondition

    // Initialize anomaly condition if it doesn't exist
    useEffect(() => {
        if (!anomalyCondition) {
            props.setAlert((prev) => {
                const newAlert = {...prev};
                const typedNewAlert = newAlert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert;
                typedNewAlert.monitorEvaluation.anomalyCondition = {
                    algorithm: "zScore",
                    zScoreParameters: {
                        operator: "greaterThan",
                        threshold: 3,
                        dataPointsInBaselineWindow: 30,
                        dataPointsInEvaluationWindow: 5,
                        dataPointsToAlarm: 3
                    }
                };
                return newAlert;
            });
        }
    }, []);

    const handleZScoreThresholdChange = (value: string) => {
        const numberValue = Number(value)
        if (isNaN(numberValue) || numberValue <= 0) return

        props.setAlert((prev) => {
            const newAlert = {...prev};
            const typedNewAlert = newAlert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert;
            if (!typedNewAlert.monitorEvaluation.anomalyCondition) {
                typedNewAlert.monitorEvaluation.anomalyCondition = {
                    algorithm: "zScore",
                    zScoreParameters: {
                        operator: anomalyCondition?.zScoreParameters?.operator || "greaterThan",
                        threshold: numberValue,
                        dataPointsInBaselineWindow: anomalyCondition?.zScoreParameters?.dataPointsInBaselineWindow || 30,
                        dataPointsInEvaluationWindow: anomalyCondition?.zScoreParameters?.dataPointsInEvaluationWindow || 5,
                        dataPointsToAlarm: anomalyCondition?.zScoreParameters?.dataPointsToAlarm || 3
                    }
                };
            } else if (typedNewAlert.monitorEvaluation.anomalyCondition.zScoreParameters) {
                typedNewAlert.monitorEvaluation.anomalyCondition.zScoreParameters.threshold = numberValue;
            }
            return newAlert;
        });
    }


    const handleEvaluationWindowChange = (value: string) => {
        const numberValue = Number(value)
        if (isNaN(numberValue) || numberValue <= 0) return

        props.setAlert((prev) => {
            const newAlert = {...prev};
            const typedNewAlert = newAlert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert;
            if (!typedNewAlert.monitorEvaluation.anomalyCondition) {
                typedNewAlert.monitorEvaluation.anomalyCondition = {
                    algorithm: "zScore",
                    zScoreParameters: {
                        operator: anomalyCondition?.zScoreParameters?.operator || "greaterThan",
                        threshold: anomalyCondition?.zScoreParameters?.threshold || 3,
                        dataPointsInBaselineWindow: anomalyCondition?.zScoreParameters?.dataPointsInBaselineWindow || 30,
                        dataPointsInEvaluationWindow: numberValue,
                        dataPointsToAlarm: anomalyCondition?.zScoreParameters?.dataPointsToAlarm || 3,
                    }
                };
            } else if (typedNewAlert.monitorEvaluation.anomalyCondition.zScoreParameters) {
                typedNewAlert.monitorEvaluation.anomalyCondition.zScoreParameters.dataPointsInEvaluationWindow = numberValue;
            }
            return newAlert;
        });
    }

    const handleDatapointsToAlarmChange = (value: string) => {
        const numberValue = Number(value)
        if (isNaN(numberValue) || numberValue <= 0) return

        props.setAlert((prev) => {
            const newAlert = {...prev};
            const typedNewAlert = newAlert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert;
            if (!typedNewAlert.monitorEvaluation.anomalyCondition) {
                typedNewAlert.monitorEvaluation.anomalyCondition = {
                    algorithm: "zScore",
                    zScoreParameters: {
                        operator: anomalyCondition?.zScoreParameters?.operator || "greaterThan",
                        threshold: anomalyCondition?.zScoreParameters?.threshold || 3,
                        dataPointsInBaselineWindow: anomalyCondition?.zScoreParameters?.dataPointsInBaselineWindow || 30,
                        dataPointsInEvaluationWindow: anomalyCondition?.zScoreParameters?.dataPointsInEvaluationWindow || 5,
                        dataPointsToAlarm: numberValue
                    }
                };
            } else if (typedNewAlert.monitorEvaluation.anomalyCondition.zScoreParameters) {
                typedNewAlert.monitorEvaluation.anomalyCondition.zScoreParameters.dataPointsToAlarm = numberValue;
            }
            return newAlert;
        });
    }





    const handleDataPointsChange = (value: string) => {
        const numberValue = Number(value)
        if (isNaN(numberValue) || numberValue <= 0) return

        props.setAlert((prev) => {
            const newAlert = {...prev};
            const typedNewAlert = newAlert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert;
            if (!typedNewAlert.monitorEvaluation.anomalyCondition) {
                typedNewAlert.monitorEvaluation.anomalyCondition = {
                    algorithm: "zScore",
                    zScoreParameters: {
                        operator: anomalyCondition?.zScoreParameters?.operator || "greaterThan",
                        threshold: anomalyCondition?.zScoreParameters?.threshold || 3,
                        dataPointsInBaselineWindow: numberValue,
                        dataPointsInEvaluationWindow: anomalyCondition?.zScoreParameters?.dataPointsInEvaluationWindow || 5,
                        dataPointsToAlarm: anomalyCondition?.zScoreParameters?.dataPointsToAlarm || 3,
                    }
                };
            } else if (typedNewAlert.monitorEvaluation.anomalyCondition.zScoreParameters) {
                typedNewAlert.monitorEvaluation.anomalyCondition.zScoreParameters.dataPointsInBaselineWindow = numberValue;
            }
            return newAlert;
        });
    }

    const handleOperatorChange = (operator: string) => {
        props.setAlert((prev) => {
            const newAlert = {...prev};
            const typedNewAlert = newAlert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert;
            if (!typedNewAlert.monitorEvaluation.anomalyCondition) {
                typedNewAlert.monitorEvaluation.anomalyCondition = {
                    algorithm: "zScore",
                    zScoreParameters: {
                        operator: operator,
                        threshold: anomalyCondition?.zScoreParameters?.threshold || 3,
                        dataPointsInBaselineWindow: anomalyCondition?.zScoreParameters?.dataPointsInBaselineWindow || 30,
                        dataPointsInEvaluationWindow: anomalyCondition?.zScoreParameters?.dataPointsInEvaluationWindow || 5,
                        dataPointsToAlarm: anomalyCondition?.zScoreParameters?.dataPointsToAlarm || 3
                    }
                };
            } else if (typedNewAlert.monitorEvaluation.anomalyCondition.zScoreParameters) {
                typedNewAlert.monitorEvaluation.anomalyCondition.zScoreParameters.operator = operator;
            }
            return newAlert;
        });
    }

    const currentOperator = anomalyCondition?.zScoreParameters?.operator || "greaterThan";

    return <div className={"flex flex-col gap-4"}>
        <div className={"flex text-textdark font-semibold items-baseline"}>
            <div className={"w-[230px]"}>
                • Anomaly Detection Settings:
            </div>
            <div className={"flex gap-2 items-center"}>
                <div className={"text-textmedium"}>Algorithm: Z-Score</div>
            </div>
        </div>
        <div className={"flex text-textdark font-semibold items-baseline"}>
            <div className={"w-[230px] pl-4"}>
                Anomaly Operator:
            </div>
            <div className={"flex gap-2 items-center"}>
                <Button
                    className={cn("h-[32px] flex justify-center items-center text-textmedium border gap-2 p-2 rounded hover:text-textlight hover:border hover:border-secondary cursor-pointer px-4",
                        currentOperator === "greaterThan" ? "bg-secondarytransparenter border-secondary" : "bg-backgroundmedium")}
                    onClick={() => handleOperatorChange("greaterThan")}>
                    Greater Than
                </Button>
                <Button
                    className={cn("h-[32px] flex justify-center items-center text-textmedium border gap-2 p-2 rounded hover:text-textlight hover:border hover:border-secondary cursor-pointer px-4",
                        currentOperator === "lessThan" ? "bg-secondarytransparenter border-secondary" : "bg-backgroundmedium")}
                    onClick={() => handleOperatorChange("lessThan")}>
                    Less Than
                </Button>
                <Button
                    className={cn("h-[32px] flex justify-center items-center text-textmedium border gap-2 p-2 rounded hover:text-textlight hover:border hover:border-secondary cursor-pointer px-4",
                        currentOperator === "greaterOrLessThan" ? "bg-secondarytransparenter border-secondary" : "bg-backgroundmedium")}
                    onClick={() => handleOperatorChange("greaterOrLessThan")}>
                    Greater or Less Than
                </Button>
            </div>
        </div>
        <div className={"flex text-textdark font-semibold items-baseline"}>
            <div className={"w-[230px] pl-4"}>
                Z-Score Threshold:
            </div>
            <div className={"flex gap-2 items-center"}>
                <Input
                    className={"w-[60px] text-center h-full p-1"}
                    type="number"
                    value={anomalyCondition?.zScoreParameters?.threshold || 3}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => handleZScoreThresholdChange(e.target.value)}
                />
                <div className={"text-textmedium text-sm"}>
                    (Higher values = less sensitive to anomalies)
                </div>
            </div>
        </div>
        <div className={"flex text-textdark font-semibold items-baseline"}>
            <div className={"w-[230px] pl-4"}>
                Data Points in Baseline Window:
            </div>
            <div className={"flex gap-2 items-center"}>
                <Input
                    className={"w-[80px] text-center h-full p-1"}
                    type="number"
                    value={anomalyCondition?.zScoreParameters?.dataPointsInBaselineWindow || 30}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => handleDataPointsChange(e.target.value)}
                />
                <div className={"text-textmedium text-sm"}>
                    (Number of data points to calculate baseline)
                </div>
            </div>
        </div>
        <div className={"flex text-textdark font-semibold items-baseline"}>
            <div className={"w-[230px] pl-4"}>
                Data Points in Evaluation Window:
            </div>
            <div className={"flex gap-2 items-center"}>
                <Input
                    className={"w-[80px] text-center h-full p-1"}
                    type="number"
                    value={anomalyCondition?.zScoreParameters?.dataPointsInEvaluationWindow || 5}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => handleEvaluationWindowChange(e.target.value)}
                />
                <div className={"text-textmedium text-sm"}>
                    (Number of data points in the evaluation window)
                </div>
            </div>
        </div>
        <div className={"flex text-textdark font-semibold items-baseline"}>
            <div className={"w-[230px] pl-4"}>
                Number of Data Points to Alarm:
            </div>
            <div className={"flex gap-2 items-center"}>
                <Input
                    className={"w-[80px] text-center h-full p-1"}
                    type="number"
                    value={anomalyCondition?.zScoreParameters?.dataPointsToAlarm || 3}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => handleDatapointsToAlarmChange(e.target.value)}
                />
                <div className={"text-textmedium text-sm"}>
                    (Number of data points that must be anomalous to trigger an alert)
                </div>
            </div>
        </div>
    </div>
}


function formatWindowUnit(alert: Alert) {
    const typedAlert = alert.alert as MetricAlert | TraceAlert | MultiMetricAlert | KubernetesResourceAlert
    const typedMonitorEvaluation = typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload
    if (typedMonitorEvaluation === undefined) {
        return WindowUnit.Minutes
    } else {
        if (typedMonitorEvaluation.windowUnit === undefined) {
            return WindowUnit.Minutes
        }
        return typedMonitorEvaluation.windowUnit
    }
}

export function getGraphAnnotationProperties(
    monitorEvaluation: MonitorEvaluation,
    timeRange: TimeRange,
    threshold: number,
    thresholdComparator: ThresholdComparator,
    currentBucketSize?: number,
    overrideStartTime?: number,
    overrideEndTime?: number,
): {
    thresholdToSet: Threshold | undefined,
    timePeriodToHighlight: TimePeriod | undefined
    annotationLabel: string
} {
    let thresholdToSet: Threshold | undefined = undefined
    let timePeriodToHighlight: TimePeriod | undefined = undefined
    let annotationLabel = ""

    if (monitorEvaluation.monitorEvaluationType === EvalType.Aggregate) {
        const aggregateEvaluationPayload = monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload
        let timeWindow = aggregateEvaluationPayload.window
        let windowUnit = aggregateEvaluationPayload.windowUnit

        let truncate = 0;
        if (currentBucketSize !== undefined) {
            if (currentBucketSize <= 60) {
                truncate += 60
            }
            truncate += currentBucketSize
        }

        let currentEndTime = Math.floor(timeRange.getStartEnd()[1].getTime() / 1000) - truncate
        timePeriodToHighlight = {
            start: overrideStartTime || currentEndTime - (timeWindow * getWindowUnitSeconds(windowUnit)),
            end: overrideEndTime || currentEndTime
        }
    } else if (monitorEvaluation.monitorEvaluationType === EvalType.Static) { // static threshold
        thresholdToSet = {
            value: String(threshold),
            comparator: thresholdComparator
        }
    }
    return {thresholdToSet, timePeriodToHighlight, annotationLabel}
}

export function getWindowUnitSeconds(windowUnit: WindowUnit) {
    switch (windowUnit) {
        case WindowUnit.Minutes:
            return 60
        case WindowUnit.Hours:
            return 3600
        case WindowUnit.Days:
            return 86400
    }
}